import React, { useRef, useState, useEffect, useCallback } from "react";
import { useAnimation } from "framer-motion";
import { StripSection, StripTrack } from "./styles";
import CarouselCard from "./CarouselCard";

const CARD_WIDTH_DESKTOP = 130;
const CARD_WIDTH_TABLET = 105;
const CARD_WIDTH_MOBILE = 85;
const GAP_DESKTOP = 16;
const GAP_MOBILE = 10;

const cardEntranceVariants = {
  hidden: { opacity: 0, y: 25, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut", delay: 0.5 + i * 0.08 },
  }),
};

function getCardDimensions() {
  if (typeof window === "undefined")
    return { width: CARD_WIDTH_DESKTOP, gap: GAP_DESKTOP };
  if (window.innerWidth <= 480)
    return { width: CARD_WIDTH_MOBILE, gap: GAP_MOBILE };
  if (window.innerWidth <= 768)
    return { width: CARD_WIDTH_TABLET, gap: GAP_DESKTOP };
  return { width: CARD_WIDTH_DESKTOP, gap: GAP_DESKTOP };
}

const CarouselStrip = ({ projects, selectedIndex, setSelectedIndex }) => {
  const containerRef = useRef(null);
  const controls = useAnimation();
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });

  const calculateConstraints = useCallback(() => {
    if (!containerRef.current) return;
    const { width, gap } = getCardDimensions();
    const stripWidth = projects.length * width + (projects.length - 1) * gap;
    const containerWidth = containerRef.current.offsetWidth;
    const overflow = stripWidth - containerWidth;
    setDragConstraints({
      left: overflow > 0 ? -overflow - 40 : 0,
      right: 0,
    });
  }, [projects.length]);

  useEffect(() => {
    calculateConstraints();
    window.addEventListener("resize", calculateConstraints);
    return () => window.removeEventListener("resize", calculateConstraints);
  }, [calculateConstraints]);

  // Auto-center selected card
  useEffect(() => {
    if (!containerRef.current) return;
    const { width, gap } = getCardDimensions();
    const containerWidth = containerRef.current.offsetWidth;
    const cardCenter = selectedIndex * (width + gap) + width / 2;
    const targetX = -(cardCenter - containerWidth / 2);
    const clampedX = Math.min(0, Math.max(dragConstraints.left, targetX));

    controls.start({
      x: clampedX,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    });
  }, [selectedIndex, controls, dragConstraints.left]);

  return (
    <StripSection ref={containerRef}>
      <StripTrack
        drag="x"
        dragConstraints={dragConstraints}
        dragElastic={0.12}
        animate={controls}
      >
        {projects.map((project, index) => (
          <CarouselCard
            key={project.id}
            project={project}
            isSelected={index === selectedIndex}
            onClick={() => setSelectedIndex(index)}
            variants={cardEntranceVariants}
            custom={index}
            initial="hidden"
            animate="visible"
          />
        ))}
      </StripTrack>
    </StripSection>
  );
};

export default CarouselStrip;
